import * as c from "@patient-watch/ui";
import {
    searchForTemplateAiWizardNavigation,
    useTemplateAiWizardSearch,
} from "@/features/ai-builder/useTemplateAiDraftStore";
import FormErrors from "@patient-watch/forms/FormErrors";
import InjectionDetailsFormFields, {
    injectionDetailLockedFields,
} from "@/features/injection/components/InjectionDetailsFormFields";
import { LockableField, LockableSectionTitle } from "@/features/templates/components/TemplateScopeLock";
import { TemplateStepFooter } from "@/features/templates/components/TemplateStepFooter";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useFinalizeTemplateWizard } from "@/features/templates/hooks/useFinalizeTemplateWizard";
import { useTemplatesCopy } from "@/features/i18n/templatesCopy";
import { useTemplateLocks } from "@/features/templates/hooks/useTemplateLocks";
import { services as s } from "@/services";
import { templateSettingsSchema } from "@patient-watch/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "@patient-watch/ui/sonner";
import { useToastCopy } from "@/features/i18n/toastCopy";
import type { z } from "zod";
import { useAuth } from "@/utils/auth";

interface RouteParams {
    injectionDetailId: string;
    templateId: string;
}

type InjectionDetailsValues = Pick<z.infer<typeof templateSettingsSchema>, "injection_details">;

const InjectionDetailsFormFieldsPage = () => {
    const auth = useAuth();
    const toastCopy = useToastCopy();
    const templatesCopy = useTemplatesCopy();
    const { templateId } = Route.useParams();
    const search = useTemplateAiWizardSearch(templateId, Route.useSearch());
    const injection_id = search.injection_id;
    const injection_details = search.injection_details;
    const { markStepComplete, setCurrentStep, getNextStepRoute, getPreviousStepRoute, currentStep } =
        useTemplateNavigationStore();
    const navigate = useNavigate();
    const { finalize, isSubmitting, isTemplateOwnerLookupBlockingSubmit } = useFinalizeTemplateWizard(
        templateId,
        search
    );
    const form = useForm<InjectionDetailsValues>({
        resolver: zodResolver(
            templateSettingsSchema.pick({
                injection_details: true,
            })
        ),
        defaultValues: {
            injection_details: injection_details ?? [
                {
                    injection_id: injection_id || undefined,
                    injection_detail_id: s.uuidv4(),
                    side: "",
                    orthopaedic_area: "",
                    site: "",
                    subsite: "",
                    injection_substance: "",
                    dose: "",
                    unit: "",
                },
            ],
        },
    });

    useEffect(() => {
        setCurrentStep("injection-details");
    }, [setCurrentStep]);

    // Rows added after the section was locked (never given a locked_fields list) start fully locked.
    const sectionLocked = useTemplateLocks(templateId).isLocked("injection_details");
    const rows = form.watch("injection_details");
    useEffect(() => {
        if (!sectionLocked) return;
        rows?.forEach((row, index) => {
            if (!row.locked_fields) {
                form.setValue(`injection_details.${index}.locked_fields`, injectionDetailLockedFields(true), {
                    shouldDirty: true,
                });
            }
        });
    }, [sectionLocked, rows, form]);

    const onSubmit = async (data: InjectionDetailsValues) => {
        try {
            markStepComplete(currentStep);
            const nextRoute = getNextStepRoute(currentStep, { templateId });
            if (nextRoute) {
                const nextSettings = { ...search, ...data };
                const wizardSearchPayload = searchForTemplateAiWizardNavigation(
                    templateId,
                    auth?.id ?? "",
                    nextSettings
                );
                if (!wizardSearchPayload) return;
                navigate({
                    ...nextRoute,
                    search: wizardSearchPayload,
                });
            } else {
                finalize(data);
            }
        } catch (error) {
            toast.error(toastCopy.failedSaveInjectionDetails);
            console.error("Form submission error:", error);
        }
    };

    const handlePrevious = () => {
        const prevRoute = getPreviousStepRoute(currentStep, { templateId });
        if (prevRoute) {
            const nextSettings = { ...search, ...form.getValues() };
            const wizardSearchPayload = searchForTemplateAiWizardNavigation(templateId, auth?.id ?? "", nextSettings);
            if (!wizardSearchPayload) return;
            navigate({
                ...prevRoute,
                search: wizardSearchPayload,
            });
        }
    };

    console.log(form.formState.errors);

    return (
        <>
            <c.ui.Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="w-full form">
                    <LockableField
                        field="injection_details"
                        onLockedChange={(locked) => {
                            const rows = form.getValues("injection_details") ?? [];
                            const lockedFields = injectionDetailLockedFields(locked);
                            rows.forEach((_, index) => {
                                form.setValue(`injection_details.${index}.locked_fields`, lockedFields, {
                                    shouldDirty: true,
                                });
                            });
                        }}
                    >
                        <LockableSectionTitle
                            title="Injection Details"
                            description="Add details about each injection site and substance"
                        />
                        <InjectionDetailsFormFields injectionId={injection_id ?? ""} />
                    </LockableField>

                    <FormErrors />

                    <TemplateStepFooter
                        onBack={handlePrevious}
                        submitPrimary
                        primaryAction="save"
                        primaryLabel={templatesCopy.saveTemplate}
                        isLoadingPrimary={isSubmitting}
                        disabledPrimary={isTemplateOwnerLookupBlockingSubmit}
                    />
                </form>
            </c.ui.Form>
        </>
    );
};

const injectionDetailsSearchSchema = templateSettingsSchema.partial().pick({
    injection_id: true,
    injection_details: true,
});

export const Route = createFileRoute("/_app/templates/$templateId/events/injection/details")({
    component: InjectionDetailsFormFieldsPage,
    validateSearch: (search) => injectionDetailsSearchSchema.parse(search),
    loader: ({ params }: { params: RouteParams }) => {
        return {
            injectionDetailId: params.injectionDetailId,
        };
    },
});
